import React, { useState, useEffect } from "react";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { 
  Users, 
  Building2, 
  CreditCard, 
  TrendingUp,
  DollarSign,
  Activity,
  Loader2,
  Star,
  Zap,
  BarChart3,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCw,
  Calendar,
  Target,
  Percent
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    try {
      const token = localStorage.getItem("responsia_token");
      const response = await axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching admin stats:", error);
      toast.error("Erreur lors du chargement des statistiques");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  const mainStatCards = [
    {
      label: "Utilisateurs",
      value: stats?.total_users || 0,
      icon: Users,
      color: "text-blue-500",
      bgColor: "bg-blue-50",
      trend: stats?.monthly?.new_users > 0 ? `+${stats.monthly.new_users} ce mois` : null,
      trendUp: true
    },
    {
      label: "Établissements",
      value: stats?.total_organizations || 0,
      icon: Building2,
      color: "text-violet-500",
      bgColor: "bg-violet-50"
    },
    {
      label: "Avis collectés",
      value: stats?.total_reviews || 0,
      icon: Activity,
      color: "text-emerald-500",
      bgColor: "bg-emerald-50",
      trend: stats?.monthly?.new_reviews > 0 ? `+${stats.monthly.new_reviews} ce mois` : null,
      trendUp: true
    },
    {
      label: "MRR",
      value: `${stats?.mrr || 0}€`,
      icon: DollarSign,
      color: "text-amber-500",
      bgColor: "bg-amber-50"
    }
  ];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-['Plus_Jakarta_Sans'] text-slate-900">
            Dashboard Admin
          </h1>
          <p className="text-slate-500 mt-1">
            Vue d'ensemble de la plateforme ResponsIA
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => fetchStats(true)}
          disabled={refreshing}
          className="self-start"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Actualiser
        </Button>
      </div>

      {/* Main Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {mainStatCards.map((stat, index) => (
          <div 
            key={index} 
            className="bg-white rounded-2xl p-5 border border-slate-100 hover:shadow-md transition-shadow"
            data-testid={`admin-stat-${stat.label.toLowerCase().replace(/\s/g, '-')}`}
          >
            <div className="flex items-start justify-between mb-3">
              <div className={`w-10 h-10 ${stat.bgColor} rounded-xl flex items-center justify-center`}>
                <stat.icon className={`w-5 h-5 ${stat.color}`} />
              </div>
              {stat.trend && (
                <span className={`text-xs flex items-center gap-0.5 ${stat.trendUp ? 'text-emerald-600' : 'text-red-500'}`}>
                  {stat.trendUp ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                  {stat.trend}
                </span>
              )}
            </div>
            <p className="text-2xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">
              {stat.value}
            </p>
            <p className="text-sm text-slate-500 mt-1">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Subscriptions & Metrics Row */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Subscriptions Breakdown */}
        <div className="bg-white rounded-2xl p-6 border border-slate-100">
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-slate-400" />
            Abonnements
          </h2>
          <div className="space-y-3">
            {/* Active subscriptions */}
            <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-xl">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <Zap className="w-4 h-4 text-emerald-600" />
                </div>
                <span className="text-sm font-medium text-slate-700">Actifs</span>
              </div>
              <span className="text-xl font-bold text-emerald-600">
                {stats?.subscriptions?.total_active || 0}
              </span>
            </div>
            
            {/* Trials */}
            <div className="flex items-center justify-between p-3 bg-amber-50 rounded-xl">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Calendar className="w-4 h-4 text-amber-600" />
                </div>
                <span className="text-sm font-medium text-slate-700">En essai</span>
              </div>
              <span className="text-xl font-bold text-amber-600">
                {stats?.subscriptions?.total_trial || 0}
              </span>
            </div>
            
            {/* Canceled */}
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center">
                  <Activity className="w-4 h-4 text-slate-500" />
                </div>
                <span className="text-sm font-medium text-slate-700">Annulés</span>
              </div>
              <span className="text-xl font-bold text-slate-500">
                {stats?.subscriptions?.total_canceled || 0}
              </span>
            </div>
            
            {/* Plan breakdown */}
            <div className="pt-3 border-t border-slate-100 mt-3">
              <p className="text-xs text-slate-500 mb-2">Répartition par plan</p>
              <div className="flex gap-4">
                <div className="flex-1 text-center p-2 bg-blue-50 rounded-lg">
                  <p className="text-lg font-bold text-blue-600">{stats?.subscriptions?.essential || 0}</p>
                  <p className="text-xs text-slate-500">Essentiel</p>
                </div>
                <div className="flex-1 text-center p-2 bg-violet-50 rounded-lg">
                  <p className="text-lg font-bold text-violet-600">{stats?.subscriptions?.premium || 0}</p>
                  <p className="text-xs text-slate-500">Premium</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Performance Metrics */}
        <div className="bg-white rounded-2xl p-6 border border-slate-100">
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Target className="w-5 h-5 text-slate-400" />
            Métriques de performance
          </h2>
          <div className="space-y-4">
            {/* Conversion Rate */}
            <div className="p-4 bg-gradient-to-r from-emerald-50 to-emerald-100 rounded-xl">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-700">Taux de conversion</span>
                <Percent className="w-4 h-4 text-emerald-500" />
              </div>
              <p className="text-3xl font-bold text-emerald-600">
                {stats?.metrics?.conversion_rate || 0}%
              </p>
              <p className="text-xs text-slate-500 mt-1">Essai → Payant</p>
            </div>
            
            {/* Churn Rate */}
            <div className="p-4 bg-gradient-to-r from-red-50 to-orange-50 rounded-xl">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-700">Taux de churn</span>
                <ArrowDownRight className="w-4 h-4 text-red-500" />
              </div>
              <p className="text-3xl font-bold text-red-500">
                {stats?.metrics?.churn_rate || 0}%
              </p>
              <p className="text-xs text-slate-500 mt-1">30 derniers jours</p>
            </div>
            
            {/* Average Rating */}
            <div className="p-4 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-xl">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-700">Note moyenne</span>
                <Star className="w-4 h-4 text-amber-500" />
              </div>
              <p className="text-3xl font-bold text-amber-600">
                {stats?.reviews_breakdown?.average_rating || 0}/5
              </p>
              <p className="text-xs text-slate-500 mt-1">Sur tous les avis</p>
            </div>
          </div>
        </div>

        {/* Revenue */}
        <div className="bg-white rounded-2xl p-6 border border-slate-100">
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-slate-400" />
            Revenus
          </h2>
          <div className="space-y-4">
            {/* Total Revenue */}
            <div className="p-4 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-xl">
              <p className="text-sm text-slate-500 mb-1">Revenu total</p>
              <p className="text-3xl font-bold text-amber-600">
                {stats?.revenue?.total || 0}€
              </p>
            </div>
            
            {/* MRR */}
            <div className="p-4 bg-slate-50 rounded-xl">
              <p className="text-sm text-slate-500 mb-1">MRR (Revenu Mensuel Récurrent)</p>
              <p className="text-2xl font-bold text-slate-800">
                {stats?.revenue?.mrr || 0}€
              </p>
            </div>
            
            {/* Revenue by plan */}
            <div className="pt-3 border-t border-slate-100">
              <p className="text-xs text-slate-500 mb-2">Par plan</p>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Essentiel (49€)</span>
                  <span className="font-semibold text-slate-800">{stats?.revenue?.essential || 0}€</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Premium (69€)</span>
                  <span className="font-semibold text-slate-800">{stats?.revenue?.premium || 0}€</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Reviews Stats & Quick Actions */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Reviews Breakdown */}
        <div className="bg-white rounded-2xl p-6 border border-slate-100">
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Star className="w-5 h-5 text-slate-400" />
            Avis clients
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 bg-emerald-50 rounded-xl text-center">
              <Star className="w-6 h-6 text-emerald-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-emerald-600">{stats?.reviews_breakdown?.positive || 0}</p>
              <p className="text-xs text-slate-500">Positifs (4-5★)</p>
            </div>
            <div className="p-4 bg-red-50 rounded-xl text-center">
              <Star className="w-6 h-6 text-red-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-red-500">{stats?.reviews_breakdown?.negative || 0}</p>
              <p className="text-xs text-slate-500">Négatifs (1-3★)</p>
            </div>
          </div>
          <div className="mt-4 p-3 bg-slate-50 rounded-xl">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600">Note moyenne estimée</span>
              <div className="flex items-center gap-1">
                <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                <span className="font-bold text-slate-800">{stats?.reviews_breakdown?.average_rating || 0}/5</span>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-2xl p-6 border border-slate-100">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Actions rapides</h2>
          <div className="space-y-3">
            <Button 
              variant="outline" 
              className="w-full justify-start rounded-xl h-12"
              onClick={() => window.location.href = '/admin/users'}
            >
              <Users className="w-5 h-5 mr-3" />
              Gérer les utilisateurs
            </Button>
            <Button 
              variant="outline" 
              className="w-full justify-start rounded-xl h-12"
              onClick={() => window.location.href = '/admin/organizations'}
            >
              <Building2 className="w-5 h-5 mr-3" />
              Gérer les établissements
            </Button>
            <Button 
              variant="outline" 
              className="w-full justify-start rounded-xl h-12"
              onClick={() => window.location.href = '/admin/subscriptions'}
            >
              <CreditCard className="w-5 h-5 mr-3" />
              Gérer les abonnements
            </Button>
            <Button 
              variant="outline" 
              className="w-full justify-start rounded-xl h-12"
              onClick={() => window.location.href = '/admin/invoices'}
            >
              <DollarSign className="w-5 h-5 mr-3" />
              Voir les factures
            </Button>
          </div>
        </div>
      </div>

      {/* Monthly Stats Banner */}
      <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-2xl p-6 text-white">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5" />
          Statistiques du mois
        </h3>
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center">
            <p className="text-3xl font-bold">{stats?.monthly?.new_users || 0}</p>
            <p className="text-sm text-amber-100">Nouveaux utilisateurs</p>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold">{stats?.monthly?.new_reviews || 0}</p>
            <p className="text-sm text-amber-100">Nouveaux avis</p>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold">{stats?.monthly?.new_subscriptions || 0}</p>
            <p className="text-sm text-amber-100">Nouveaux abonnements</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
